<template>
  <div
    class="fixed left-0 top-0 z-50 flex h-full w-screen items-center justify-center backdrop-blur-[1px] backdrop-brightness-50"
  >
    <div
      class="flex h-full w-full flex-col bg-white p-6 transition-all duration-300 ease-in-out dark:bg-dark-1 md:h-[95vh] max-w-4xl-custom md:rounded-xl 2xl:h-[90vh]"
    >
      <header
        class="relative shrink-0 pb-2 text-center dark:border-dark-2 keyboard-open:hidden"
      >
        <div class="text-3xl font-bold">Auftrag abschließen</div>

        <button
          type="button"
          class="absolute right-[-5px] top-[-6px] z-10 ml-auto inline-flex items-center rounded-lg bg-transparent px-2 py-1.5 text-xl text-gray-400 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-dark-4 dark:hover:text-white"
          data-modal-hide="popup-modal"
          data-testId="closeDialogBtn"
          @click="openCancel"
        >
          <em class="mdi mdi-close" />
        </button>
      </header>
      <div class="grow overflow-auto pr-2">
        <div class="flex flex-col gap-3">
          <BaseInformation
            class="-mb-px"
            status="warning"
            information-text="Ein abgeschlossener Auftrag kann nicht nachträglich bearbeitet werden. Bist Du sicher, dass alle notwendigen Angaben korrekt und vollständig erfasst wurden?"
          />
          <BaseSection heading="Besondere Hinweise zum Auftrag">
            <BaseTextarea
              v-model:input-text="order.detail.note"
              label="Notizen"
              :max-length="NOTE_MAX_LENGTH"
              test-id="textarea_note"
            />
          </BaseSection>
          <BaseSection heading="Fotos">
            <ImageCapture
              ref="imageCaptureRef"
              :object-id="order.id"
              :object-type="ImageObjectType.ORDER"
              :capture-image-information="true"
              a
            />
          </BaseSection>
          <BaseSection
            v-if="!inspectionPointStore.hasSuccessfulGatewayLocation()"
          >
            <Checkbox
              v-model:checked="order.propertyCompatibility.isCompatible"
              label="Die Ausstattung aller Gebäude mit SRT ist nicht möglich."
              :value="order.propertyCompatibility.isCompatible"
              :is-inverted="true"
              :disabled="allMatchedNotCompatible"
              test-id="isCompatible"
              :validation="v$.order.propertyCompatibility.isCompatible"
              @update:checked="handleIsPropertyCompatible($event)"
            />
            <BaseDropdown
              v-if="!isPropertyCompatible && !allMatchedNotCompatible"
              v-model:selection="order.propertyCompatibility.reason"
              label="Grund"
              dropdown-text="Grund wählen"
              :options-list="propertyCompatibilityReasonList"
              :validation="v$.order.propertyCompatibility.reason"
            />
            <BaseTextarea
              v-if="!isPropertyCompatible && !allMatchedNotCompatible"
              v-model:input-text="order.propertyCompatibility.note"
              label="Notizen"
              :optional="true"
              test-id="textarea_compatibleNote"
              class="pt-0"
              :max-length="NOTE_MAX_LENGTH"
              :validation="v$.order.propertyCompatibility.note"
            />
          </BaseSection>
        </div>
      </div>
      <footer
        class="flex shrink-0 items-end justify-end gap-4 pt-2 dark:border-dark-2 keyboard-open:hidden"
      >
        <TertiaryButton
          label="Abbrechen"
          data-testid="button-cancel"
          @click="openCancel"
        />
        <PrimaryButton
          label="Auftrag abschließen"
          :is-muted="!isFormValid"
          data-testid="button-save"
          class="mr-0"
          @click="openPrompt"
        />
        <BasePrompt
          :title="i18n.cancelPrompt.title"
          :question="i18n.cancelPrompt.question"
          :proceed="closeForm"
          :cancel="closeCancel"
          :close="closeCancel"
          :open="isCancelOpen"
          :proceed-text="i18n.cancelPrompt.proceedText"
          :cancel-text="i18n.cancelPrompt.cancelText"
        />
        <BasePrompt
          :open="isMissingInfoOpen"
          :close="closeMissingInfo"
          :cancel="closeMissingInfo"
          :proceed="proceedMissingInfo"
          :title="i18n.missingBuildingInformationPrompt.title"
          :question="i18n.missingBuildingInformationPrompt.question"
          :cancel-text="i18n.missingBuildingInformationPrompt.cancelText"
          :proceed-text="i18n.missingBuildingInformationPrompt.proceedText"
          icon="mdi-alert-outline text-light-error dark:text-dark-error"
        >
          <div
            v-for="address in missingBuildingAddresses.values()"
            :key="address.id"
          >
            <span class="font-bold"
              >{{ address.street }} {{ address.houseNumber }}</span
            >
          </div>
        </BasePrompt>
        <BasePrompt
          :open="isCompatibilityOpen"
          :close="closeCompatibility"
          :cancel="closeCompatibility"
          :proceed="proceedCompatibility"
          :title="i18n.propertyCompatibilityPrompt.title"
          :question="i18n.propertyCompatibilityPrompt.question"
          :cancel-text="i18n.propertyCompatibilityPrompt.cancelText"
          :proceed-text="i18n.propertyCompatibilityPrompt.proceedText"
          icon="mdi-alert-outline text-light-warning dark:text-dark-warning"
        >
          <div
            v-for="address in orderAddresses.values()"
            :key="address.id + 'order'"
            class="mt-2"
          >
            <span class="font-bold"
              >{{ address.street }} {{ address.houseNumber }}</span
            >
          </div>
        </BasePrompt>
      </footer>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeMount, onMounted, ref, Ref } from 'vue';
import { useFormStore } from '@/store/form/formStore';
import TertiaryButton from '@/components/UI/Button/TertiaryButton.vue';
import PrimaryButton from '@/components/UI/Button/PrimaryButton.vue';
import { useOrderStore } from '@/store/order/orderStore';
import BasePrompt from '@/components/UI/Modal/BasePrompt.vue';
import i18n from '@/utils/i18n/form.json';
import { useBuildingStore } from '@/store/entities/buildingStore';
import { ImageObjectType } from '@/models/Image';
import { propertyCompatibilityReasonList } from '@/enums/order';
import { NOTE_MAX_LENGTH } from '@/enums/generic';
import BaseTextarea from '@/components/UI/Note/BaseTextarea.vue';
import BaseDropdown from '@/components/UI/Dropdown/BaseDropdown.vue';
import BaseInformation from '@/components/UI/Hint/BaseInformation.vue';
import Checkbox from '@/components/UI/Checkbox/Checkbox.vue';
import BaseSection from '@/components/UI/Card/BaseSection.vue';
import ImageCapture, {
  ImageCaptureRef,
} from '@/components/Forms/DeviceFormComponents/ImageCapture.vue';
import { useInspectionPointStore } from '@/store/entities/inspectionPointStore';
import { useOrderForm } from '@/composables/useOrderForm';

const imageCaptureRef: Ref<ImageCaptureRef | null> = ref(null);

const inspectionPointStore = useInspectionPointStore();
const formStore = useFormStore();
const buildingStore = useBuildingStore();
const orderStore = useOrderStore();

const allMatchedNotCompatible = computed(() => {
  return buildingStore.areAllMatchedOrderAddressesNotCompatible();
});
const missingBuildingAddresses = computed(() =>
  buildingStore.getOrderAddressesWithoutBuildingMatch()
);

const orderAddresses = computed(() => orderStore.getOrderAddresses());

const {
  order,
  v$,
  isFormValid,
  isPropertyCompatible,
  isCancelOpen,
  isMissingInfoOpen,
  isCompatibilityOpen,
  openCancel,
  closeCancel,
  openPrompt,
  closeMissingInfo,
  proceedMissingInfo,
  closeCompatibility,
  proceedCompatibility,
  handleIsPropertyCompatible,
  closeForm,
  initializeOrderForm,
  trackOpen,
} = useOrderForm({ imageCaptureRef });

onBeforeMount(() => {
  initializeOrderForm();
  trackOpen();
});

onMounted(() => {
  formStore.resetValidation();
  if (allMatchedNotCompatible.value && order.value.propertyCompatibility) {
    order.value.propertyCompatibility.isCompatible = false;
  }
});
</script>
